import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Divider, Stack } from '@esgian/esgianui';
import KPIChangeDisplay from '@components/Display/KPIChangeDisplay/KPIChangeDisplay';
import { getPercentDiff, getValue } from '@helpers';
import { findDrillingEvent } from '@api/helpers';
import { useSelector } from 'react-redux';
import { getDisplayUnit } from '@store/features';
import { useDisplayUnit } from '@hooks';

function DrillingActivitiesKpis({ mainData, baselineData, loading, activeActivityTypes }) {
  const displayUnit = useSelector(getDisplayUnit);
  const { getKpiDisplayText, valueWithUnit } = useDisplayUnit();

  const getSumData = (main, baseline, activity) => {
    const mainItem = main.find((item) => item.activity === activity);
    const baseItem = baseline.find((item) => item.activity === activity);
    
    const mainEvent = findDrillingEvent(mainItem?.events, 'Drilling', displayUnit);
    const baseEvent = findDrillingEvent(baseItem?.events, 'Drilling', displayUnit);
    
    const mainData = mainEvent?.timeSeriesValues || {};
    const baseData = baseEvent?.timeSeriesValues || {};

    let mainTot = 0;
    let baseTot = 0;
    Object.values(mainData).forEach((value) => {
      mainTot += getValue(value);
    });
    Object.values(baseData).forEach((value) => {
      baseTot += getValue(value);
    });

    return {
      ...valueWithUnit(mainTot - baseTot, displayUnit, true),
      percent: getPercentDiff(baseTot, mainTot)
    };
  };

  const kpis = useMemo(() => {
    if (!mainData || !baselineData || !activeActivityTypes?.length) return {};

    let mainSum = 0;
    let baseSum = 0;
    
    mainData.forEach((item) => {
      const event = findDrillingEvent(item.events, 'Drilling', displayUnit);
      Object.values(event?.timeSeriesValues || {}).forEach((value) => {
        mainSum += getValue(value);
      });
    });

    if (mainSum === 0) {
      return {
        total: { value: null, title: null, percent: null },
        topDifferences: []
      };
    }

    baselineData.forEach((item) => {
      const event = findDrillingEvent(item.events, 'Drilling', displayUnit);
      Object.values(event?.timeSeriesValues || {}).forEach((value) => {
        baseSum += getValue(value);
      });
    });

    let topDifferences = getTopDifferences();

    return {
      total: {
        ...valueWithUnit(mainSum - baseSum, displayUnit, true),
        percent: getPercentDiff(baseSum, mainSum)
      },
      topDifferences: Object.values(topDifferences)
    };
  }, [mainData, baselineData, displayUnit, activeActivityTypes]);

  return (
    <Stack
      direction={'row'}
      justifyContent={'space-evenly'}
      divider={<Divider sx={{ height: 'inherit' }} orientation={'vertical'} />}>
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.total?.percent}
        value={kpis.total?.value ?? null}
        unit={kpis.total?.title}
        title={getKpiDisplayText('Drilling equipment')}
        tooltipText={'This figure indicates the total drilling equipment difference between the selected rig and the baseline at the end of the comparison.'}
        tooltipEnterDelayMs={500}
      />
      {kpis.topDifferences?.map((difference) => (
          <KPIChangeDisplay
          key={`drilling-activity-difference-${difference.formattedName}`}
          loading={loading}
          percentChange={difference?.sum.percent}
          value={difference?.sum.value ?? null}
          unit={difference?.sum.title}
          title={difference?.name}
          tooltipText={`This figure indicates the total drilling equipment difference between the selected rig and the baseline during ${difference?.name} activity.`}
          tooltipEnterDelayMs={500}
        />
      ))}
    </Stack>
  );

  function getTopDifferences() {
    let mainDataTotalConsumptionByActivity = [];
    mainData.forEach((item) => {
      const event = findDrillingEvent(item.events, 'Drilling', displayUnit);
      let totalConsumption = 0;
      Object.values(event?.timeSeriesValues || {}).forEach((value) => {
        totalConsumption += getValue(value);
      });
      mainDataTotalConsumptionByActivity.push({ activity: item.activity, totalConsumption: totalConsumption });
    });
    let topActivitiesByConsumption = mainDataTotalConsumptionByActivity
      .sort((a, b) => b.totalConsumption - a.totalConsumption)
      .slice(0, 4)
      .map(act => act.activity);

    let activitiesConsumptionDiffs = activeActivityTypes.map((act) => {
      return {
        name: act.name,
        formattedName: act.name.replace(/[^a-zA-Z\d]/g, '-').replace(/-+/g, '-').toLowerCase(),
        sum: getSumData(mainData, baselineData, act.name)
      };
    });

    let topDifferences = topActivitiesByConsumption
      .map(actName => activitiesConsumptionDiffs.find(cd => cd.name === actName)
      );
    return topDifferences;
  }
}

DrillingActivitiesKpis.propTypes = {
  mainData: PropTypes.arrayOf(PropTypes.object),
  baselineData: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool
};

DrillingActivitiesKpis.defaultProps = {
  mainData: [],
  baselineData: [],
  loading: false
};

export default DrillingActivitiesKpis;
